"use client";

import { useState } from "react";
import axios from "axios";
import { downloadBase64AsZip, downloadContent } from "../utils/file-utils";
import { API_BASE_URL } from "../services/api/endpoints";
import type { MigrationResponse } from "../types/migrations";
import { extractApiErrorMessage } from "../utils/error-handling";

interface UseFileDownloadProps {
  migrationResult: MigrationResponse | null;
}

interface UseFileDownloadResult {
  isDownloading: boolean;
  downloadError: string | null;
  downloadSuccess: string | null;
  handleDownload: (fileTypeParam: string, fileName?: string) => Promise<void>;
}

/**
 * Custom hook for handling file downloads
 */
export function useFileDownload({ migrationResult }: UseFileDownloadProps): UseFileDownloadResult {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  const handleDownload = async (fileTypeParam: string, fileName?: string) => {
    try {
      console.log(
        `handleDownload - Starting download: ${fileTypeParam}, sourceFileName: ${fileName || "not provided"}`
      );
      setIsDownloading(true);
      setDownloadError(null);
      setDownloadSuccess(null);

      if (!migrationResult || !migrationResult.success) {
        const errorMsg = "No migration results available";
        console.error(errorMsg);
        setDownloadError(errorMsg);
        setIsDownloading(false);
        return;
      }

      if (!fileName) {
        const errorMsg = "File Name is not valid.";
        console.error(errorMsg);
        setDownloadError(errorMsg);
        setIsDownloading(false);
        return;
      }

      // Get migration ID from result
      const migrationId = migrationResult.content?.id || migrationResult.content?.migrationId;

      if (!migrationId) {
        const errorMsg = "Could not determine migration ID";
        console.error(errorMsg);
        setDownloadError(errorMsg);
        setIsDownloading(false);
        return;
      }

      try {
        const url = `${API_BASE_URL}/api/v1/migrations/${migrationId}/files/${encodeURIComponent(
          fileName
        )}?file_type=${fileTypeParam}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
          responseType: "json",
          validateStatus: () => true,
        });

        if (response.status >= 200 && response.status < 300) {
          if (fileName.includes(".zip")) {
            await downloadBase64AsZip(response.data.content, fileName);
          } else {
            await downloadContent(response.data.content, fileName);
          }

          const successMsg = `File ${fileName} downloaded successfully`;
          setDownloadSuccess(successMsg);
        } else {
          throw new Error(`Error in direct request: Status ${response.status}`);
        }
      } catch (error) {
        const errorMsg = `Error downloading file: ${extractApiErrorMessage(error)}`;
        console.error(errorMsg);
        setDownloadError(errorMsg);
      }
    } catch (error) {
      const errorMsg = `General error: ${extractApiErrorMessage(error)}`;
      console.error(errorMsg);
      setDownloadError(errorMsg);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    isDownloading,
    downloadError,
    downloadSuccess,
    handleDownload,
  };
}
